import { useEffect, useRef, useState } from 'react';
import { updateBotHandler } from '../../actions/botHandler';
import { requestLivechat, transferToBot, transferToWhatsapp } from '../../utilities/LiveChat';
import { removeTempMessages, resetMessages, updateFlow, updateReplyMessage, updateTextInput } from '../../actions';
import { updateAudioInputsState, updateEnvironment } from '../../actions/environment.action';
import { useDispatch, useSelector } from 'react-redux';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SpeechRecognition from 'react-speech-recognition';
import { stopVoiceOutput } from '../../services/shared';

const ChatWindowOptions = (props: any) => {
  const { color, shouldDisplayOptionInTyping } = props;
  const newRef = useRef(null);
  const [showOptions, setShowOptions] = useState(false);
  const environment = useSelector((state: any) => state?.environment);
  const configuration = useSelector((state: any) => state?.configuration);
  const revisitFlow = useSelector((state: any) => state.revisitFlow);
  const dispatch = useDispatch();

  const { refreshChatText, transferToWhatsAppText, startLiveChatText, transferToBotText, toWhatsapp } =
    configuration?.chatMenu || {};
  const { chatRequestAccepted, agentId } = environment || {};

  let disableWhatsappBtn: boolean;
  if (localStorage.getItem('agentId') && localStorage.getItem('bp-wsn')) {
    disableWhatsappBtn = false;
  } else {
    disableWhatsappBtn = true;
  }

  const isTypingOrStreaming: boolean = (environment.typing || environment.ai.streaming)
  ? true 
  : false;

  const transferChatToWhatsapp = () => {
    if (!disableWhatsappBtn || !toWhatsapp) {
      transferToWhatsapp();
      setShowOptions(false);
    }
  };

  const startLiveChat = () => {
    setShowOptions(false);
    if (chatRequestAccepted) {
      dispatch(updateBotHandler('BOT'));
      dispatch(updateReplyMessage(null));
      return transferToBot();
    }
    return requestLivechat();
  };

  const restartChat = () => {
    const { clearChatOnRestart } = configuration?.chatMenu || {};

    /**
     * Author: Satyam Sharma
     * Date: 23 January 2025
     * 1. Remove temp messages if any added from the auido comp
     * 2. Cancel any speech recognition subscription to prevent leakage
     * 3. Stop audio output when the chat is refreshed
     * 4. Initialize the state of mic if it was listening
     */
    dispatch(removeTempMessages());
    SpeechRecognition.stopListening();
    stopVoiceOutput();
    dispatch(updateAudioInputsState({ isMicOn: false, isListening: false }));


    const currentTimerRef = environment.timerRef;

    if (currentTimerRef) {
      clearTimeout(currentTimerRef);
    }
    // resetting local messages to [] on clicking restart button
    if (clearChatOnRestart) {
      dispatch(resetMessages());
    }

    dispatch(updateFlow([revisitFlow]));
    dispatch(updateTextInput({ value: '', original: '' }));
    dispatch(updateBotHandler('BOT'));
    dispatch(updateEnvironment({ selectedTags: [], batchedMessages: '', currentTimerRef: null, activeMID: '' }));
    transferToBot(true);
    setShowOptions(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });

  const handleOutsideClick = (e: any) => {
    if (newRef.current && !(newRef as any).current.contains(e.target)) {
      setShowOptions(false);
    }
  };


  /**
   * Author: Satyam Sharma
   * Date: 28 Jan 2025
   * Description: Switch between voice and chat input as per activeInput in the REDUX store
   * Active Input is a temp variable
   */

  const switchVoiceInputSettings = () => {
    dispatch(removeTempMessages());
    dispatch(updateEnvironment({
      activeInput: environment?.activeInput === 'voice' ? 'chat' : 'voice'
    }));
    setShowOptions(false);
  }

  return (
    <div id='ele-190' className="cursor-pointer rounded-full relative">
      <MoreVertIcon
        onClick={() => setShowOptions(!showOptions)}
        sx={{ width: '32px', height: '32px', color: color }}
        className={`hover:opacity-85 !text-2xl ${color} align-middle text-center rounded-full hover:border`}
      />

      {/* Transfer to whatsapp button */}
      {showOptions && (
        <div
          id='ele-191'
          className={`absolute  ${environment?.direction === 'ltr' ? 'right-0' : 'left-0'} ${shouldDisplayOptionInTyping && 'bottom-0'}  bg-white w-52 flex flex-col text-primaryText shadow-xl text-sm rounded-lg items-start z-10`}
          ref={newRef}
        >
          {!configuration?.chatMenu?.hideTransferToWhatsApp && (
            <div
              id='ele-192'
              className={`w-full ${!agentId && 'cursor-default opacity-60'}`}
              onClick={() => transferChatToWhatsapp()}
            >
              <div id='ele-193' className="w-full py-3 px-4">
                <span id='ele-194'>
                  <img
                    src={process.env.REACT_APP_CDN + '/whatsapp.svg'}
                    alt="whatsapp"
                    className="inline-block w-4"
                  />
                </span>
                <span id='ele-195' className={`${environment?.direction === 'ltr' ? 'ml-2' : 'mr-2'}`}>{transferToWhatsAppText || 'Transfer to Whatsapp'}</span>
              </div>
              <hr id='ele-196' className="w-5/6 h-px float-right bg-gray-100 border-0 rounded" />
            </div>
          )}

          {/* Start Live Chat button */}
          {configuration?.chatMenu?.toLiveChat && (
            <div
              id='ele-197'
              className="w-full"
              style={{
                pointerEvents: isTypingOrStreaming ? 'none' : 'auto',
                opacity: isTypingOrStreaming ? 0.5 : 1,
              }}
            
              onClick={() => startLiveChat()}
            >
              <div id='ele-198' className="px-4 py-3 w-full">
                <span id='ele-199'>
                  <img
                    src={process.env.REACT_APP_CDN + '/live-chat.svg'}
                    alt="chat"
                    className="inline-block w-4"
                  />
                </span>
                {chatRequestAccepted === false ? (
                  <span id='ele-200' className={`${environment?.direction === 'ltr' ? 'ml-2' : 'mr-2'}`}>{startLiveChatText || 'Start Live Chat'}</span>
                ) : (
                  <span id='ele-201' className={`${environment?.direction === 'ltr' ? 'ml-2' : 'mr-2'}`}>{transferToBotText || 'Transfer To Bot'}</span>
                )}
              </div>
              <hr id='ele-202' className="w-5/6 h-px float-right bg-gray-100 border-0 rounded" />
            </div>
          )}

          {/* Refresh Chat button */}

          {configuration?.chatMenu?.refreshChat && (
            <div
              id='ele-203'
              className="px-4 py-3 w-full"
              style={{
                pointerEvents: isTypingOrStreaming ? 'none' : 'auto',
                opacity: isTypingOrStreaming ? 0.5 : 1,
              }}            
              onClick={() => restartChat()}
            >
              <span id='ele-204'>
                <img
                  src={process.env.REACT_APP_CDN + '/refresh-chat.svg'}
                  alt="refresh"
                  className="inline-block w-4"
                />
              </span>
              <span id='ele-205' className={`${environment?.direction === 'ltr' ? 'ml-2' : 'mr-2'}`}>{refreshChatText || 'Refresh Chat'}</span>
            </div>
          )}

          {/* Voice Support */}
          {
            configuration?.chatMenu?.voiceSupport?.isEnabled && environment.activeQuestion.type.toLowerCase() === 'ai' && (
              <div
              id='ele-411'
              className="px-4 py-3 w-full"
              style={{
                pointerEvents: isTypingOrStreaming ? 'none' : 'auto',
                opacity: isTypingOrStreaming ? 0.5 : 1,
              }}            
              onClick={() => switchVoiceInputSettings()}
            >
              <span id='ele-412'>
                <img
                  src={process.env.REACT_APP_CDN + '/swap_horiz.svg'}
                  alt="refresh"
                  className="inline-block w-4"
                />
              </span>
              <span id='ele-205' className={`${environment?.direction === 'ltr' ? 'ml-2' : 'mr-2'}`}>{`Switch to ${environment?.activeInput === 'voice' ? 'Chat' : 'Voice'} Input`}</span>
            </div>
            )
          }
        </div>
      )}
    </div>
  );
};

ChatWindowOptions.defaultProps = {
  shouldDisplayOptionInTyping: false
}

export default ChatWindowOptions;
